// Wizard-Hub-Hooks (S50): Entwurfs-Batches + Sidebar-Badge für
// reviewbereite Batches (Muster: useNotificationBadges).
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../../../../lib/api";
import { useRealtimeRecoveryInterval } from "../../../realtime/realtimeEvents";
import { actionBadgeQueryOptions } from "../../../../hooks/actionBadgeQuery";
import type {
  ListingDraftBatch,
  ListingDraftListFilters,
  ListingDraftListResponse,
  ListingDraftSummary,
} from "../types";

export const draftKeys = {
  all: ["listing-drafts"] as const,
  list: (filters: ListingDraftListFilters = {}) => ["listing-drafts", "list", filters] as const,
  count: () => ["listing-drafts", "count"] as const,
  detail: (id: string) => ["listing-drafts", "detail", id] as const,
};

function pageIsVisible(): boolean {
  return typeof document === "undefined" || document.visibilityState === "visible";
}

export function listingDraftListNeedsPolling(batches: ListingDraftSummary[] | undefined): boolean {
  return Boolean(
    batches?.some(
      (batch) =>
        batch.status === "generating" ||
        batch.status === "creating" ||
        (batch.status === "created" &&
          batch.images?.enabled === true &&
          batch.images.ready < batch.images.total),
    ),
  );
}

export function useListingDrafts(
  filters: ListingDraftListFilters = {},
  options: { enabled?: boolean } = {},
) {
  const recoveryInterval = useRealtimeRecoveryInterval();
  return useQuery<ListingDraftListResponse>({
    queryKey: draftKeys.list(filters),
    queryFn: ({ signal }) =>
      api.get("/api/listing-drafts", { params: filters, signal }).then((r) => r.data),
    enabled: options.enabled ?? true,
    placeholderData: (previous) => previous,
    // Der gemeinsame SSE-Stream invalidiert nach jedem Worker-Schritt. Das
    // sichtbarkeitsgebundene REST-Abfrage bleibt als langsamer Recovery-Pfad.
    refetchInterval: (query) =>
      pageIsVisible() &&
      (listingDraftListNeedsPolling(query.state.data?.batches) ||
        (query.state.data?.imageQueue?.open ?? 0) > 0)
        ? recoveryInterval
        : false,
  });
}

export function useListingDraft(id: string | undefined) {
  const recoveryInterval = useRealtimeRecoveryInterval();
  return useQuery<ListingDraftBatch>({
    queryKey: draftKeys.detail(id || ""),
    queryFn: () => api.get(`/api/listing-drafts/${id}`).then((r) => r.data.batch),
    enabled: Boolean(id),
    // Die Row bleibt die Progress-Wahrheit; SSE stößt den normalen Refetch an.
    refetchInterval: (query) =>
      pageIsVisible() &&
      (query.state.data?.status === "generating" ||
        query.state.data?.status === "creating" ||
        (query.state.data?.status === "created" &&
          (query.state.data.readiness?.articles.total ?? 0) >
            (query.state.data.readiness?.articles.pushReady ?? 0)))
        ? recoveryInterval
        : false,
  });
}

export function useReviewableDraftCount({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery<number>({
    ...actionBadgeQueryOptions,
    queryKey: draftKeys.count(),
    queryFn: ({ signal }) =>
      api.get("/api/listing-drafts/count", { signal }).then((r) => r.data.reviewCount ?? 0),
    enabled,
  });
}

export function useInvalidateDrafts() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: draftKeys.all });
}
